import React from "react";
import { Box, Typography, TextField, Stack, MenuItem } from "@mui/material";
import { theme } from "../../theme/colors";
import { isPoliticianOfficeAccount, isCandidateAccount } from "../../utils/accountTypes";

const politicalParties = [
  { value: "democrat", label: "Democrat" },
  { value: "republican", "label": "Republican" },
  { value: "independent", "label": "Independent" },
  { value: "libertarian", "label": "Libertarian" },
  { value: "green", "label": "Green" },
  { value: "other", "label": "Other" },
  { value: "prefer_not_to_say", "label": "Prefer not to say" }
];

const occupations = [
  { value: "student", label: "Student" },
  { value: "educator", label: "Educator / Teacher" },
  { value: "healthcare", label: "Healthcare Worker" },
  { value: "tech", label: "Technology / IT" },
  { value: "business", label: "Business / Finance" },
  { value: "government", label: "Government / Public Sector" },
  { value: "nonprofit", label: "Nonprofit / NGO" },
  { value: "legal", label: "Legal / Law" },
  { value: "trades", label: "Skilled Trades" },
  { value: "service", label: "Service Industry" },
  { value: "arts", label: "Arts / Entertainment" },
  { value: "retired", label: "Retired" },
  { value: "unemployed", label: "Unemployed" },
  { value: "other", label: "Other" }
];

const homeStatus = [
  { value: "renter", label: "Renter" },
  { value: "owner", label: "Owner" },
  { value: "houseless", label: "Houseless" }
];

const genderIdentity = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-binary" },
  { value: "self_describe", label: "Self-describe" },
  { value: "prefer_not_to_say", label: "Prefer not to say" }
];

const ageRanges = [
  { value: "under_18", label: "18 and under" },
  { value: "18_24", label: "18-24" },
  { value: "25_34", label: "25-34" },
  { value: "35_44", label: "35-44" },
  { value: "45_54", label: "45-54" },
  { value: "55_64", label: "55-64" },
  { value: "65_74", label: "65-74" },
  { value: "75_plus", label: "75+" }
];

const organizationTypes = [
  { value: "nonprofit", label: "Nonprofit / 501(c)(3)" },
  { value: "business", label: "Business" },
  { value: "community", label: "Community Group" },
  { value: "campaign", label: "Political Campaign" },
  { value: "union", label: "Labor Union" },
  { value: "advocacy", label: "Advocacy Organization" },
  { value: "other", label: "Other" }
];

const organizationSubtypesAndCategories = [
  { value: "advocacy", label: "Advocacy & Activism" },
  { value: "service", label: "Service & Support" },
  { value: "education", label: "Education" },
  { value: "religious", label: "Religious" },
  { value: "labor", label: "Labor Union" },
  { value: "professional", label: "Professional" },
  { value: "community", label: "Community Group" },
  { value: "political", label: "Political" },
  { value: "other", label: "Other" }
];

const organizationSizes = [
  { value: "1_10", label: "1-10" },
  { value: "11_50", "label": "11-50" },
  { value: "51_200", "label": "51-200" },
  { value: "201_1000", "label": "201-1000" },
  { value: "1000_plus", "label": "1000+" }
];

const militaryStatus = [
  { value: "veteran", label: "Veteran" },
  { value: "active_duty", label: "Active Duty" },
  { value: "not_applicable", label: "N/A" }
];

const parentCaregiverStatus = [
  { value: "parent", label: "Parent" },
  { value: "caregiver", label: "Caregiver" },
  { value: "not_applicable", label: "Not Applicable" }
];

const fieldSx = {
  "& .MuiInputLabel-root": { color: "#334155" },
  "& .MuiInputLabel-root.Mui-focused": { color: theme.primary },
  "& .MuiOutlinedInput-input": { color: "#0f172a" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary, borderWidth: 2 },
};

const officeStatusLabels = {
  incumbent: "Currently in Office",
  former: "Previously Held Office",
  first_time: "First-Time Candidate",
};

const officeLevelLabels = {
  local: "Local (City/Town/County)",
  state: "State",
  federal: "Federal",
};

const positionLabels = {
  us_senator: "U.S. Senator",
  us_representative: "U.S. Representative",
  state_senator: "State Senator",
  state_representative: "State Representative",
  governor: "Governor",
  lt_governor: "Lieutenant Governor",
  attorney_general: "Attorney General",
  secretary_of_state: "Secretary of State",
  state_treasurer: "State Treasurer / Comptroller",
  mayor: "Mayor",
  city_council: "City Council Member",
  county_commissioner: "County Commissioner",
  school_board: "School Board Member",
};

const readOnlyFieldSx = {
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .MuiOutlinedInput-input": { color: "#0f172a" },
  "& .MuiOutlinedInput-root": { backgroundColor: "rgba(0, 0, 0, 0.02)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0", borderWidth: 1 },
};

export default function CivixProfileSection({ formData, handleChange, accountTypeName }) {
  const isCitizen = accountTypeName === "citizen" || accountTypeName === "user" || !accountTypeName;
  const isPolitician = isPoliticianOfficeAccount(accountTypeName);
  const isCandidate = isCandidateAccount(accountTypeName);
  const isOrg = ['organization', 'non_profit', 'municipality'].includes(accountTypeName);

  const renderSelect = (label, field, options) => (
    <TextField
      fullWidth
      select
      label={label}
      value={formData[field] || ""}
      onChange={handleChange(field)}
      variant="outlined"
      size="small"
      sx={fieldSx}
    >
      <MenuItem value="">
        <em>Select...</em>
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box id="civix-profile" sx={{ mb: 4, scrollMarginTop: "88px" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 2, fontSize: "1.125rem" }}>
        {isOrg ? "Organization Profile" : isPolitician || isCandidate ? "Political Profile" : "Civix Profile"}
      </Typography>

      <Stack spacing={2.5}>
        {/* ORGANIZATION FIELDS */}
        {isOrg && (
          <>
            <TextField
              fullWidth
              label="Entity Name"
              value={formData.entity_name || ""}
              onChange={handleChange("entity_name")}
              variant="outlined"
              size="small"
              sx={fieldSx}
            />
            {renderSelect("Organization Category", "organization_type", organizationTypes)}
            {formData.organization_type === "other" && (
              <TextField
                fullWidth
                label="Other Category"
                value={formData.organization_type_other || ""}
                onChange={handleChange("organization_type_other")}
                variant="outlined"
                size="small"
                sx={fieldSx}
              />
            )}
            <TextField
              fullWidth
              label="Mission / About"
              multiline
              rows={3}
              value={formData.organization_mission || ""}
              onChange={handleChange("organization_mission")}
              variant="outlined"
              size="small"
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Public Contact Email"
              value={formData.organization_contact_email || ""}
              onChange={handleChange("organization_contact_email")}
              variant="outlined"
              size="small"
              sx={fieldSx}
            />
            {renderSelect("Focus Area", "organization_subtype", organizationSubtypesAndCategories)}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Year Founded"
                type="number"
                value={formData.year_founded || ""}
                onChange={handleChange("year_founded")}
                variant="outlined"
                size="small"
                sx={fieldSx}
              />
              {renderSelect("Organization Size", "organization_size", organizationSizes)}
            </Stack>
          </>
        )}

        {/* POLITICIAN / CANDIDATE FIELDS */}
        {(isPolitician || isCandidate) && (
          <>
            {formData.public_office_status && (
              <TextField
                fullWidth
                label="Office Status"
                value={officeStatusLabels[formData.public_office_status] || formData.public_office_status}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            )}
            {formData.public_office_level && (
              <TextField
                fullWidth
                label="Office Level"
                value={officeLevelLabels[formData.public_office_level] || formData.public_office_level}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            )}
            {formData.public_office_position && (
              <TextField
                fullWidth
                label="Position"
                value={positionLabels[formData.public_office_position] || formData.public_office_position}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={readOnlyFieldSx}
              />
            )}
            <TextField
              fullWidth
              label="Campaign Website"
              value={formData.campaign_website || ""}
              onChange={handleChange("campaign_website")}
              variant="outlined"
              size="small"
              sx={fieldSx}
            />
            {isPolitician && (
              <>
                {renderSelect("Political Party", "political_party", politicalParties)}
                {formData.political_party === "other" && (
                  <TextField
                    fullWidth
                    label="Other Party"
                    value={formData.political_party_other || ""}
                    onChange={handleChange("political_party_other")}
                    variant="outlined"
                    size="small"
                  />
                )}
              </>
            )}
            
            {isPolitician && (
              <>
                <TextField
                  fullWidth
                  label="Current Office"
                  value={formData.current_office || ""}
                  onChange={handleChange("current_office")}
                  variant="outlined"
                  size="small"
                  sx={fieldSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    label="District / Jurisdiction"
                    value={formData.district_jurisdiction || ""}
                    onChange={handleChange("district_jurisdiction")}
                    variant="outlined"
                    size="small"
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Years in Office"
                    type="number"
                    value={formData.years_in_office || ""}
                    onChange={handleChange("years_in_office")}
                    variant="outlined"
                    size="small"
                    sx={fieldSx}
                  />
                </Stack>
              </>
            )}

            {isCandidate && (
              <>
                <TextField
                  fullWidth
                  label="Office Seeking"
                  value={formData.office_seeking || ""}
                  onChange={handleChange("office_seeking")}
                  variant="outlined"
                  size="small"
                  sx={fieldSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Election Year"
                    type="number"
                    value={formData.election_year || ""}
                    onChange={handleChange("election_year")}
                    variant="outlined"
                    size="small"
                    sx={fieldSx}
                  />
                </Stack>
              </>
            )}
          </>
        )}

        {/* CITIZEN FIELDS */}
        {isCitizen && (
          <>
            {renderSelect("Occupation", "occupation", occupations)}
            {formData.occupation === "other" && (
              <TextField
                fullWidth
                label="Other Occupation"
                value={formData.occupation_other || ""}
                onChange={handleChange("occupation_other")}
                variant="outlined"
                size="small"
                sx={fieldSx}
              />
            )}
            {renderSelect("Home Status", "home_status", homeStatus)}
            {renderSelect("Age Range", "age_range", ageRanges)}
            {renderSelect("Parent / Caregiver Status", "parent_caregiver_status", parentCaregiverStatus)}
          </>
        )}

        {/* DEMOGRAPHICS (Shared by Citizen, Politician, Candidate) */}
        {!isOrg && (
          <>
            {renderSelect("Gender Identity", "gender_identity", genderIdentity)}
            {formData.gender_identity === "self_describe" && (
              <TextField
                fullWidth
                label="Self-Describe Gender"
                value={formData.gender_identity_other || ""}
                onChange={handleChange("gender_identity_other")}
                variant="outlined"
                size="small"
                sx={fieldSx}
              />
            )}
            {renderSelect("Veteran / Military Status", "veteran_military_status", militaryStatus)}
            {/* Privacy MVP: relationship status stays in the database but is
                intentionally not collected in account management. */}
          </>
        )}
      </Stack>
    </Box>
  );
}